import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ConstructionIcon from '@mui/icons-material/Construction';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PublicPageShell from '../components/PublicPageShell';

const PRIMARY = '#0087c1';

const VALUES = [
  {
    icon: ConstructionIcon,
    title: 'Built for Construction',
    text: 'Every feature in Struxor is designed around how construction teams actually work — on site, in the office, and everywhere in between. We don\'t bolt construction onto generic business software; we build it from the ground up.',
  },
  {
    icon: GroupsIcon,
    title: 'Teams of All Sizes',
    text: 'Whether you\'re a one-person operation or a 35-strong organisation, Struxor scales with you. From solo contractors to large companies — the same powerful tools, the same simple pricing.',
  },
  {
    icon: RocketLaunchIcon,
    title: 'AI That Takes Action',
    text: 'Other platforms use AI to surface data. We believe AI should do the work. Struxor\'s AI assistant generates quotes, creates forms, drafts emails, and analyses project data — directly inside Struxor.',
  },
  {
    icon: HandshakeIcon,
    title: 'Transparent & Fair',
    text: 'Flat monthly pricing with no per-user fees and no annual lock-in. We believe construction companies deserve straightforward software pricing — so that\'s exactly what we offer.',
  },
];

export default function About() {
  const theme = useTheme();

  return (
    <PublicPageShell>
      <Helmet>
        <title>About Struxor — Construction Technology for Every Team</title>
        <meta
          name="description"
          content="Struxor is construction software built for teams of all sizes. Learn about our mission to help construction companies manage quotes, projects, and site collaboration from anywhere."
        />
        <link rel="canonical" href="https://www.struxorapp.com/about" />
        <meta property="og:title" content="About Struxor — Construction Technology for Every Team" />
        <meta property="og:description" content="Struxor is construction software built for teams of all sizes. Learn about our mission to help construction companies manage quotes, projects, and site collaboration from anywhere." />
        <meta property="og:url" content="https://www.struxorapp.com/about" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About Struxor',
          description: 'Struxor is construction software built for teams of all sizes — from sole traders to large organisations.',
          url: 'https://www.struxorapp.com/about',
          isPartOf: { '@type': 'WebSite', name: 'Struxor', url: 'https://www.struxorapp.com' },
        })}</script>
      </Helmet>

      {/* Hero */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, color: '#fff', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" fontWeight={800} sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
            Construction software that works the way you do
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 650, mx: 'auto', fontSize: { xs: '1rem', md: '1.15rem' } }}>
            Struxor is built by people who understand construction — and believe every team deserves powerful, affordable tools.
          </Typography>
        </Container>
      </Box>

      {/* Story */}
      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" fontWeight={700} mb={3} sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Why we built Struxor
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
              Construction teams deal with complexity every day — managing quotes and tenders, coordinating site team members, tracking project progress, maintaining safety compliance, and keeping clients informed. Yet most project management tools are either designed for other industries or priced so high that only large enterprises can afford them.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
              Struxor was created to change that. We set out to build all-in-one construction software that handles the full project lifecycle — from the first quote to final handover — while remaining accessible to teams of every size.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              With an AI assistant that takes action (not just answers questions), built-in digital forms, timesheets, purchase orders, RAMS management, and a native mobile app with offline access, Struxor puts everything your team needs in one place — at a price that makes sense.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                background: `linear-gradient(135deg, ${alpha(PRIMARY, 0.08)} 0%, ${alpha(PRIMARY, 0.02)} 100%)`,
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
                p: { xs: 3, md: 5 },
              }}
            >
              <Stack spacing={3}>
                {[
                  { label: 'Mission', text: 'Give every construction team — from sole traders to large organisations — access to the same powerful project management tools, at a fair price.' },
                  { label: 'Approach', text: 'Build for the field first. If it doesn\'t work on site with one hand while holding a coffee, it\'s not ready.' },
                  { label: 'Commitment', text: 'Transparent pricing, no lock-in contracts, no surprises. Your data is always yours, and you can export or cancel at any time.' },
                ].map((item) => (
                  <Box key={item.label}>
                    <Typography variant="overline" sx={{ color: PRIMARY, fontWeight: 700, letterSpacing: 1.5 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mt: 0.5 }}>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Values */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : alpha(theme.palette.background.paper, 0.5), borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container sx={{ py: { xs: 6, md: 10 } }}>
          <Typography variant="h4" component="h2" align="center" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
            What drives us
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 5, maxWidth: 600, mx: 'auto' }}>
            Four principles that shape every feature we build and every decision we make.
          </Typography>
          <Grid container spacing={3}>
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <Grid item xs={12} sm={6} key={v.title}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 4,
                      transition: 'transform 200ms ease, box-shadow 200ms ease',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 24px rgba(0,0,0,0.08)' },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 3,
                          background: alpha(PRIMARY, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <Icon sx={{ fontSize: 28, color: PRIMARY }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        {v.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {v.text}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, py: { xs: 6, md: 8 }, textAlign: 'center', color: '#fff' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={700} mb={2} sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Ready to see what Struxor can do?
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
            14-day free trial. Unlimited users. No credit card required.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              size="large"
              sx={{ background: '#fff', color: PRIMARY, fontWeight: 700, textTransform: 'none', borderRadius: 2, px: 4, '&:hover': { background: alpha('#fff', 0.9) } }}
            >
              Start Free Trial
            </Button>
            <Button
              component={RouterLink}
              to="/contact"
              variant="outlined"
              size="large"
              sx={{ borderColor: 'rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600, textTransform: 'none', borderRadius: 2, px: 4, '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' } }}
            >
              Get in Touch
            </Button>
          </Stack>
        </Container>
      </Box>
    </PublicPageShell>
  );
}
